// engagement-components/TemplateManager.jsx
import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Modal,
  Alert,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaWhatsapp,
  FaSms,
  FaEnvelope,
  FaLink,
  FaArrowRight,
  FaEye,
  FaFileAlt,
  FaPoll,
  FaGlobe,
  FaTimes,
} from "react-icons/fa";
import CryptoJS from "crypto-js";
import axios from "axios";
import { apiUrl, authHeaders, uploadHeaders } from "./config";

// ─── Mirrors feedback/feedback-components/config.jsx exactly ─────────────────
const surveyBaseUrl =
  process.env.REACT_APP_SURVEY_BASE_URL || "https://secure.netshineoffice.com";

const generateSurveyLink = (surveyId) => {
  const companyId = localStorage.getItem("companyid");
  const encryptedOrgId = companyId
    ? encodeURIComponent(
        CryptoJS.AES.encrypt(companyId, "ns0-200200221").toString()
      )
    : "your-restaurant-id";
  const key = encodeURIComponent(`${encryptedOrgId}-${surveyId}`);
  return `${surveyBaseUrl}/feedback.html?${key}`;
};

// ─── Constants ────────────────────────────────────────────────────────────────
const CHANNELS = ["WhatsApp", "SMS", "Email"];

const VARIABLES = [
  { v: "{{guest_name}}", desc: "Guest's full name" },
  { v: "{{hotel_name}}", desc: "Property name" },
  { v: "{{checkin_date}}", desc: "Check-in date" },
  { v: "{{checkout_date}}", desc: "Check-out date" },
  { v: "{{room_number}}", desc: "Room number" },
  {
    v: "{{link}}",
    desc: "Tracked link — landing page, feedback form, or custom URL",
  },
];

const CHANNEL_COLOR = {
  WhatsApp: "#198754",
  SMS: "#6c757d",
  Email: "#0d6efd",
};

const LINK_TYPES = [
  {
    id: "landing",
    label: "Offer Page",
    icon: <FaFileAlt />,
    color: "#0d6efd",
    desc: "Link to a landing page you built — images, pricing, CTA buttons",
  },
  {
    id: "feedback",
    label: "Feedback Form",
    icon: <FaPoll />,
    color: "#198754",
    desc: "Link to one of your existing feedback surveys",
  },
  {
    id: "custom",
    label: "Custom URL",
    icon: <FaGlobe />,
    color: "#6c757d",
    desc: "Any external URL — booking engine, website, social",
  },
  {
    id: "none",
    label: "No Link",
    icon: <FaTimes />,
    color: "#adb5bd",
    desc: "Message only — no {{link}} variable",
  },
];

const emptyTemplate = () => ({
  id: null,
  name: "",
  channel: "WhatsApp",
  content: "",
  linkType: "none",
  landingPageId: "",
  surveyId: "",
  customUrl: "",
  headerImage: "",
  status: "Active",
});

// ─── Resolve actual link URL ──────────────────────────────────────────────────
const resolveLink = (template, landingPages, surveys) => {
  if (!template) return null;

  switch (template.linkType) {
    case "landing": {
      const lp = landingPages.find((x) => String(x.id) === String(template.landingPageId));
      return lp
        ? { label: lp.title, url: `/o/${lp.slug}`, type: "landing", obj: lp }
        : null;
    }

    case "feedback": {
      const sv = surveys.find(
        (x) => String(x.surveyid) === String(template.surveyId)
      );
      return sv
        ? {
            label: sv.title,
            url: generateSurveyLink(sv.surveyid),
            type: "feedback",
            obj: sv,
          }
        : null;
    }

    case "custom":
      return template.customUrl
        ? {
            label: template.customUrl,
            url: template.customUrl,
            type: "custom",
            obj: null,
          }
        : null;

    default:
      return null;
  }
};

// ─── Message Preview ──────────────────────────────────────────────────────────
const MessagePreview = ({ template, link }) => {
  const displayUrl = link
    ? link.type === "feedback"
      ? "hotel.co/feedback?key=..."
      : link.type === "landing"
      ? `hotel.co/o/${link.obj?.slug || "offer"}`
      : link.url
    : null;

  const msg = (template?.content || "")
    .replace("{{guest_name}}", "Rahul Sharma")
    .replace("{{hotel_name}}", "Grand Netshine Hotel")
    .replace("{{checkin_date}}", "15 Jan 2025")
    .replace("{{checkout_date}}", "17 Jan 2025")
    .replace("{{room_number}}", "204")
    .replace("{{link}}", displayUrl || "hotel.co/link");

  const empty = !template?.content;
  const ch = template?.channel || "WhatsApp";

  if (ch === "WhatsApp") {
    return (
      <div
        className="p-3 rounded"
        style={{ background: "#e5ddd5", minHeight: 100 }}
      >
        {empty ? (
          <div className="text-center text-muted py-3">
            <FaWhatsapp
              size={22}
              className="mb-2 d-block mx-auto"
              color="#adb5bd"
            />
            <small>Start typing to see WhatsApp preview</small>
          </div>
        ) : (
          <div
            className="ms-auto p-3"
            style={{
              background: "#dcf8c6",
              maxWidth: "92%",
              fontSize: "0.85rem",
              lineHeight: 1.6,
              borderRadius: "12px 0 12px 12px",
              whiteSpace: "pre-wrap",
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
            }}
          >
            {msg}

            {link && (
              <div
                className="mt-2 pt-2 border-top border-success"
                style={{ fontSize: "0.75rem" }}
              >
                {link.type === "feedback" && (
                  <FaPoll className="me-1 text-success" />
                )}
                {link.type === "landing" && (
                  <FaFileAlt className="me-1 text-primary" />
                )}
                {link.type === "custom" && (
                  <FaGlobe className="me-1 text-secondary" />
                )}
                <span className="text-primary">{displayUrl}</span>
                <span className="text-muted ms-2">→ {link.label}</span>
              </div>
            )}

            <div
              className="text-end text-muted mt-1"
              style={{ fontSize: "0.68rem" }}
            >
              09:00 ✓✓
            </div>
          </div>
        )}
      </div>
    );
  }

  if (ch === "SMS") {
    return (
      <div
        className="p-3 rounded border"
        style={{
          background: "#f8f9fa",
          minHeight: 80,
          fontSize: "0.85rem",
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
        }}
      >
        {empty ? (
          <span className="text-muted fst-italic">SMS preview...</span>
        ) : (
          msg
        )}
        {!empty && link && (
          <div className="text-primary mt-1" style={{ fontSize: "0.75rem" }}>
            {displayUrl}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border rounded overflow-hidden" style={{ minHeight: 80 }}>
      <div
        className="bg-light px-3 py-2 border-bottom"
        style={{ fontSize: "0.72rem", color: "#6c757d" }}
      >
        From: hotel@netshine.com
      </div>
      <div
        className="p-3"
        style={{
          fontSize: "0.85rem",
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
        }}
      >
        {empty ? (
          <span className="text-muted fst-italic">Email preview...</span>
        ) : (
          msg
        )}
        {!empty && link && (
          <div className="mt-2">
            <a href="#!" className="text-primary" style={{ fontSize: "0.8rem" }}>
              {displayUrl} — {link.label}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TemplateManager = ({ onNavigate }) => {
  const [templates, setTemplates] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [filterCh, setFilterCh] = useState("All");
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tRes, lpRes, svRes] = await Promise.all([
        axios.get(`${apiUrl}/feedback/templates`, authHeaders()),
        axios.get(`${apiUrl}/feedback/landing-pages`, authHeaders()),
        axios.get(`${apiUrl}/feedback/surveys`, authHeaders()),
      ]);

      setTemplates(
        (tRes.data || []).map((t) => ({
          ...t,
          id: t.templateid || t.id,
          linkType: t.link_type || t.linkType || "none",
          landingPageId: t.landing_page_id || t.landingPageId || "",
          surveyId: t.survey_id || t.surveyId || "",
          customUrl: t.custom_url || t.customUrl || "",
          headerImage: t.header_image || t.headerImage || "",
          status: t.status || "Active",
        }))
      );

      setLandingPages(
        (lpRes.data || []).map((p) => ({
          ...p,
          id: p.pageid || p.id,
        }))
      );

      const rawSurveys = Array.isArray(svRes.data) ? svRes.data : [];
      setSurveys(
        rawSurveys.map((s) => ({
          surveyid: s.surveyid || s.id,
          title: s.title || "Untitled Survey",
          status: s.status || "Draft",
          responses: s.total_responses || s.responses || 0,
        }))
      );
    } catch (e) {
      setTemplates([]);
      setLandingPages([]);
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(emptyTemplate());
    setShowEditor(true);
    setError("");
  };

  const openEdit = (t) => {
    setEditing({
      ...emptyTemplate(),
      ...t,
      id: t.templateid || t.id,
      linkType: t.link_type || t.linkType || "none",
      landingPageId: t.landing_page_id || t.landingPageId || "",
      surveyId: t.survey_id || t.surveyId || "",
      customUrl: t.custom_url || t.customUrl || "",
      headerImage: t.header_image || t.headerImage || "",
      status: t.status || "Active",
    });
    setShowEditor(true);
    setError("");
  };

  const insertVariable = (v) => {
    setEditing((p) => ({
      ...p,
      content: `${p.content || ""}${v}`,
    }));
  };

  const save = async () => {
    if (!editing.name.trim()) {
      setError("Template name is required.");
      return;
    }

    if (!editing.content.trim()) {
      setError("Message content is required.");
      return;
    }

    if (editing.linkType !== "none" && !editing.content.includes("{{link}}")) {
      setError(
        "You selected a link type but {{link}} is missing from your message. Add {{link}} where you want the URL to appear."
      );
      return;
    }

    setSaving(true);
    setError("");

    const payload = {
      name: editing.name,
      channel: editing.channel,
      content: editing.content,
      link_type: editing.linkType,
      landing_page_id: editing.landingPageId || null,
      survey_id: editing.surveyId || null,
      custom_url: editing.customUrl || null,
      header_image: editing.headerImage || null,
      status: editing.status || "Active",
    };

    const id = editing.templateid || editing.id;

    try {
      if (id) {
        await axios.put(
          `${apiUrl}/feedback/templates/${id}`,
          payload,
          authHeaders()
        );

        setTemplates((prev) =>
          prev.map((t) =>
            (t.templateid || t.id) === id
              ? { ...t, ...editing, id }
              : t
          )
        );
      } else {
        const res = await axios.post(
          `${apiUrl}/feedback/templates`,
          payload,
          authHeaders()
        );

        const saved = res.data
          ? {
              ...res.data,
              id: res.data.templateid || res.data.id,
              linkType: res.data.link_type || res.data.linkType || editing.linkType,
              landingPageId:
                res.data.landing_page_id || res.data.landingPageId || editing.landingPageId,
              surveyId: res.data.survey_id || res.data.surveyId || editing.surveyId,
              customUrl: res.data.custom_url || res.data.customUrl || editing.customUrl,
              headerImage:
                res.data.header_image || res.data.headerImage || editing.headerImage,
            }
          : { ...editing, id: `t${Date.now()}` };

        setTemplates((prev) => [saved, ...prev]);
      }

      setSuccess("Template saved!");
      setShowEditor(false);
      setEditing(null);
    } catch (e) {
      const saved = { ...editing, id: id || `t${Date.now()}` };

      setTemplates((prev) =>
        id
          ? prev.map((t) => ((t.templateid || t.id) === id ? saved : t))
          : [saved, ...prev]
      );

      setShowEditor(false);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async () => {
    const id = confirmData?.templateid || confirmData?.id;

    try {
      await axios.delete(`${apiUrl}/feedback/templates/${id}`, authHeaders());
    } catch (e) {}

    setTemplates((prev) => prev.filter((t) => (t.templateid || t.id) !== id));
    setShowConfirm(false);
    setConfirmData(null);
  };

  const resolvedLink = resolveLink(editing, landingPages, surveys);
  const hasLinkInMsg = editing?.content?.includes("{{link}}");

  const filtered =
    filterCh === "All"
      ? templates
      : templates.filter((t) => t.channel === filterCh);

  const renderCardLinkBadge = (t) => {
    const link = resolveLink(t, landingPages, surveys);

    if (link?.type === "feedback") {
      return (
        <div
          className="p-2 rounded d-flex align-items-center gap-2 mt-2"
          style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            fontSize: "0.78rem",
          }}
        >
          <FaPoll className="text-success flex-shrink-0" />
          <div className="flex-grow-1 overflow-hidden">
            <div className="fw-semibold text-success text-truncate">
              {link.label}
            </div>
            <div className="text-muted">Feedback survey</div>
          </div>
        </div>
      );
    }

    if (link?.type === "landing") {
      return (
        <div
          className="p-2 rounded d-flex align-items-center gap-2 mt-2"
          style={{
            background: "#f0f4ff",
            border: "1px solid #c7d2fe",
            fontSize: "0.78rem",
          }}
        >
          <FaFileAlt className="text-primary flex-shrink-0" />
          <div className="flex-grow-1 overflow-hidden">
            <div className="fw-semibold text-primary text-truncate">
              {link.label}
            </div>
            <div className="text-muted">Offer page</div>
          </div>
        </div>
      );
    }

    if (link?.type === "custom") {
      return (
        <div
          className="p-2 rounded d-flex align-items-center gap-2 mt-2"
          style={{
            background: "#f8f9fa",
            border: "1px solid #dee2e6",
            fontSize: "0.78rem",
          }}
        >
          <FaGlobe className="text-secondary flex-shrink-0" />
          <div className="text-truncate text-muted">{link.url}</div>
        </div>
      );
    }

    if (t.content?.includes("{{link}}") && t.linkType === "none") {
      return (
        <div
          className="p-2 rounded d-flex align-items-center gap-2 mt-2"
          style={{
            background: "#fff7ed",
            border: "1px solid #fed7aa",
            fontSize: "0.78rem",
          }}
        >
          <FaLink className="text-warning flex-shrink-0" />
          <span className="text-warning fw-semibold">
            {"{{link}}"} used — no destination set
          </span>
          <Button
            variant="link"
            size="sm"
            className="p-0 ms-auto text-warning fw-semibold text-decoration-none"
            onClick={() => openEdit(t)}
          >
            Attach →
          </Button>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h3 className="fw-bold text-dark mb-0">
              <i className="fas fa-file-alt text-primary me-2"></i>
              Templates
            </h3>
            <p className="text-muted mb-0 mt-1">
              Message templates — link to offer pages, feedback forms, or any URL
            </p>
          </div>

          <div className="d-flex gap-2 align-items-center flex-wrap">
            <div className="d-flex gap-1 flex-wrap">
              {["All", ...CHANNELS].map((ch) => (
                <Button
                  key={ch}
                  size="sm"
                  variant={filterCh === ch ? "primary" : "outline-secondary"}
                  className="rounded-pill px-3"
                  onClick={() => setFilterCh(ch)}
                >
                  {ch}
                </Button>
              ))}
            </div>

            <Button variant="primary" className="px-4" onClick={openCreate}>
              <FaPlus className="me-2" />
              New Template
            </Button>
          </div>
        </div>
      </div>

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Alert variant="info" className="d-flex align-items-start gap-3 mb-4">
        <i
          className="fas fa-lightbulb text-info mt-1"
          style={{ fontSize: "1.1rem" }}
        ></i>
        <div style={{ fontSize: "0.84rem" }}>
          <strong>Tip:</strong> Add <code>{"{{link}}"}</code> to your message,
          then choose what it opens — a <strong>Feedback Survey</strong>, an{" "}
          <strong>Offer Page</strong>, or any custom URL. Every tap is tracked
          per guest.
        </div>
      </Alert>

      {/* Template Cards */}
      <Row className="g-4">
        {filtered.map((t) => (
          <Col md={6} lg={4} key={t.id}>
            <Card className="shadow-sm border-0 h-100 template-card">
              <Card.Body className="p-4 d-flex flex-column">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h6 className="fw-bold text-dark mb-0">{t.name}</h6>
                  <Badge
                    style={{ background: CHANNEL_COLOR[t.channel] }}
                    className="ms-2 flex-shrink-0"
                  >
                    {t.channel === "WhatsApp" && <FaWhatsapp className="me-1" />}
                    {t.channel === "SMS" && <FaSms className="me-1" />}
                    {t.channel === "Email" && <FaEnvelope className="me-1" />}
                    {t.channel}
                  </Badge>
                </div>

                <p
                  className="text-muted small flex-grow-1 mb-2"
                  style={{
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    lineHeight: 1.5,
                  }}
                >
                  {t.content}
                </p>

                {renderCardLinkBadge(t)}
              </Card.Body>

              <Card.Footer className="bg-white border-top d-flex justify-content-end gap-2 px-4 py-3">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Edit template</Tooltip>}
                >
                  <Button
                    variant="light"
                    size="sm"
                    className="p-2"
                    onClick={() => openEdit(t)}
                  >
                    <FaEdit className="text-primary" />
                  </Button>
                </OverlayTrigger>

                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Delete</Tooltip>}
                >
                  <Button
                    variant="light"
                    size="sm"
                    className="p-2"
                    onClick={() => {
                      setConfirmData(t);
                      setShowConfirm(true);
                    }}
                  >
                    <FaTrash className="text-danger" />
                  </Button>
                </OverlayTrigger>
              </Card.Footer>
            </Card>
          </Col>
        ))}

        <Col md={6} lg={4}>
          <Card
            className="shadow-sm border-0 h-100 text-center d-flex align-items-center justify-content-center template-blank-card"
            style={{ minHeight: 180, cursor: "pointer" }}
            onClick={openCreate}
          >
            <Card.Body>
              <h6 className="fw-bold text-primary mb-2">New Template</h6>
              <p className="text-muted small mb-3">
                Create a message and link it to a feedback form, offer page, or URL
              </p>
              <FaPlus className="text-primary" style={{ fontSize: "1.5rem" }} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Full height right panel */}
      {showEditor && editing && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1055,
            display: "flex",
            alignItems: "stretch",
          }}
        >
          <div
            onClick={() => setShowEditor(false)}
            style={{
              flex: 1,
              background: "rgba(0,0,0,0.4)",
              cursor: "pointer",
            }}
          />

          <div
            style={{
              width: "60vw",
              minWidth: 720,
              maxWidth: "100%",
              flexShrink: 0,
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              height: "100vh",
              boxShadow: "-8px 0 48px rgba(0,0,0,0.15)",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "16px 24px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "1rem",
                    fontWeight: 800,
                    color: "#111827",
                  }}
                >
                  {editing.id ? "Edit Template" : "New Template"}
                </div>
                <div style={{ fontSize: "0.73rem", color: "#9ca3af" }}>
                  {editing.channel} · {editing.content?.length || 0} chars
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => setShowEditor(false)}
                  style={{
                    padding: "7px 16px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    background: "#fff",
                    color: "#374151",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>

                <button
                  onClick={save}
                  disabled={saving}
                  style={{
                    padding: "7px 20px",
                    borderRadius: 8,
                    border: "none",
                    background: "#4f46e5",
                    color: "#fff",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? "Saving…" : "Save Template"}
                </button>

                <button
                  onClick={() => setShowEditor(false)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    border: "1px solid #e5e7eb",
                    background: "#f9fafb",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.1rem",
                    color: "#6b7280",
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {error && (
              <div
                style={{
                  padding: "10px 24px",
                  background: "#fef2f2",
                  borderBottom: "1px solid #fecaca",
                  fontSize: "0.82rem",
                  color: "#dc2626",
                  flexShrink: 0,
                }}
              >
                {error}
              </div>
            )}

            {/* Body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
              <Row className="g-4">
                {/* Left */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      Template Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={editing.name}
                      onChange={(e) =>
                        setEditing((p) => ({ ...p, name: e.target.value }))
                      }
                      placeholder="e.g. Birthday WhatsApp"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Channel</Form.Label>
                    <div className="d-flex gap-2 flex-wrap">
                      {CHANNELS.map((ch) => (
                        <Button
                          key={ch}
                          size="sm"
                          variant={
                            editing.channel === ch
                              ? "primary"
                              : "outline-secondary"
                          }
                          className="rounded-pill px-3"
                          onClick={() =>
                            setEditing((p) => ({ ...p, channel: ch }))
                          }
                        >
                          {ch === "WhatsApp" && <FaWhatsapp className="me-1" />}
                          {ch === "SMS" && <FaSms className="me-1" />}
                          {ch === "Email" && <FaEnvelope className="me-1" />}
                          {ch}
                        </Button>
                      ))}
                    </div>
                  </Form.Group>

                  {editing.channel === "WhatsApp" && (
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Header Image <span style={{ fontSize:"0.72rem", color:"#9ca3af", fontWeight:400 }}>(optional)</span>
                      </Form.Label>
                      <div style={{ fontSize:"0.74rem", color:"#6b7280", marginBottom:8 }}>
                        WhatsApp supports a header image above the message. Upload a file or paste a CDN URL.
                      </div>

                      {/* ── Existing image preview ── */}
                      {editing.headerImage && (
                        <div style={{ borderRadius:10, overflow:"hidden",
                          border:"1px solid #e5e7eb", marginBottom:10,
                          position:"relative" }}>
                          <img src={editing.headerImage} alt="Header"
                            style={{ width:"100%", maxHeight:140,
                              objectFit:"cover", display:"block" }}
                            onError={e => { e.target.style.display = "none"; }} />
                          <div style={{ display:"flex", justifyContent:"space-between",
                            alignItems:"center", padding:"6px 10px",
                            background:"#f8fafc", borderTop:"1px solid #e5e7eb" }}>
                            <span style={{ fontSize:"0.7rem", color:"#9ca3af",
                              overflow:"hidden", textOverflow:"ellipsis",
                              whiteSpace:"nowrap", maxWidth:260 }}>
                              {editing.headerImage}
                            </span>
                            <button onClick={() => setEditing(p => ({ ...p, headerImage:"" }))}
                              style={{ border:"none", background:"#fee2e2",
                                color:"#dc2626", fontSize:"0.72rem", fontWeight:700,
                                padding:"3px 10px", borderRadius:6, cursor:"pointer",
                                flexShrink:0, marginLeft:8 }}>
                              ✕ Remove
                            </button>
                          </div>
                        </div>
                      )}

                      {/* ── Upload button + URL input ── */}
                      {!editing.headerImage && (
                        <div style={{ display:"flex", gap:8, alignItems:"stretch" }}>
                          {/* File upload */}
                          <label style={{ display:"inline-flex", alignItems:"center",
                            gap:6, padding:"7px 14px", borderRadius:8,
                            border:"1.5px solid #4f46e5", background:"#eef2ff",
                            color:"#4f46e5", fontSize:"0.8rem", fontWeight:700,
                            cursor: uploadingImage ? "not-allowed" : "pointer",
                            opacity: uploadingImage ? 0.6 : 1, flexShrink:0 }}>
                            {uploadingImage
                              ? <><Spinner animation="border" size="sm" /> Uploading…</>
                              : <><i className="fas fa-upload" style={{ fontSize:"0.76rem" }} /> Upload Image</>}
                            <input type="file" accept="image/*"
                              style={{ display:"none" }}
                              disabled={uploadingImage}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setUploadingImage(true);
                                try {
                                  const fd = new FormData();
                                  fd.append("image", file);
                                  const res = await axios.post(
                                    `${apiUrl}/feedback/templates/upload-image`,
                                    fd,
                                    uploadHeaders()
                                  );
                                  if (res.data?.url) {
                                    setEditing(p => ({ ...p, headerImage: res.data.url }));
                                  }
                                } catch {
                                  setError("Image upload failed. Check file size (max 2 MB) and try again.");
                                } finally {
                                  setUploadingImage(false);
                                  e.target.value = "";
                                }
                              }} />
                          </label>

                          {/* Or paste URL */}
                          <input type="url"
                            value={editing.headerImage || ""}
                            onChange={e => setEditing(p => ({ ...p, headerImage: e.target.value }))}
                            placeholder="or paste CDN URL…"
                            style={{ flex:1, padding:"7px 11px", fontSize:"0.82rem",
                              border:"1px solid #e5e7eb", borderRadius:8,
                              outline:"none", color:"#111827" }}
                            onFocus={e => { e.target.style.borderColor="#4f46e5"; }}
                            onBlur={e  => { e.target.style.borderColor="#e5e7eb"; }} />
                        </div>
                      )}

                      <div style={{ fontSize:"0.7rem", color:"#9ca3af", marginTop:5 }}>
                        Max 2 MB · JPG, PNG, GIF, WEBP · Uploaded to your CDN
                      </div>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-2">
                    <Form.Label className="fw-semibold">
                      Message Content <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={editing.content}
                      onChange={(e) =>
                        setEditing((p) => ({ ...p, content: e.target.value }))
                      }
                      placeholder={`Hi {{guest_name}},\n\nThank you for staying at {{hotel_name}}!\n\nWe'd love your feedback:\n{{link}}\n\nThank you! 🙏`}
                      style={{ resize: "vertical", fontSize: "0.88rem" }}
                    />

                    <div className="d-flex justify-content-between mt-1 flex-wrap gap-2">
                      <Form.Text className="text-muted">
                        {editing.content.length} chars
                      </Form.Text>

                      {editing.linkType !== "none" &&
                        !hasLinkInMsg &&
                        editing.content.length > 0 && (
                          <Form.Text className="text-danger fw-semibold">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            Add {"{{link}}"} to message
                          </Form.Text>
                        )}

                      {hasLinkInMsg && (
                        <Form.Text className="text-success">
                          <i className="fas fa-check-circle me-1"></i>
                          {"{{link}}"} ✓
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>

                  <div className="mb-4">
                    <Form.Label className="fw-semibold">
                      Insert Variable
                    </Form.Label>
                    <div className="d-flex flex-wrap gap-1">
                      {VARIABLES.map(({ v, desc }) => (
                        <OverlayTrigger
                          key={v}
                          placement="top"
                          overlay={<Tooltip>{desc}</Tooltip>}
                        >
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="rounded-pill"
                            style={{ fontSize: "0.75rem" }}
                            onClick={() => insertVariable(v)}
                          >
                            {v}
                          </Button>
                        </OverlayTrigger>
                      ))}
                    </div>
                  </div>

                  <div
                    className="p-3 rounded border"
                    style={{
                      background: "#f8faff",
                      borderColor: "#c7d2fe",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <FaLink className="text-primary" />
                      <span className="fw-semibold">
                        {"{{link}}"} destination
                      </span>
                    </div>

                    <Row className="g-2 mb-3">
                      {LINK_TYPES.map((lt) => (
                        <Col xs={6} key={lt.id}>
                          <div
                            className="p-2 rounded border text-center"
                            style={{
                              cursor: "pointer",
                              borderColor:
                                editing.linkType === lt.id
                                  ? lt.color
                                  : "#dee2e6",
                              background:
                                editing.linkType === lt.id
                                  ? `${lt.color}12`
                                  : "#fff",
                              transition: "all 0.15s",
                            }}
                            onClick={() =>
                              setEditing((p) => ({
                                ...p,
                                linkType: lt.id,
                                landingPageId:
                                  lt.id !== "landing" ? "" : p.landingPageId,
                                surveyId:
                                  lt.id !== "feedback" ? "" : p.surveyId,
                                customUrl:
                                  lt.id !== "custom" ? "" : p.customUrl,
                              }))
                            }
                            role="button"
                            aria-pressed={editing.linkType === lt.id}
                          >
                            <div style={{ color: lt.color, fontSize: "1rem" }}>
                              {lt.icon}
                            </div>
                            <div
                              className="fw-semibold mt-1"
                              style={{
                                fontSize: "0.78rem",
                                color: lt.color,
                              }}
                            >
                              {lt.label}
                            </div>
                            <div
                              className="text-muted"
                              style={{
                                fontSize: "0.68rem",
                                lineHeight: 1.3,
                              }}
                            >
                              {lt.desc}
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>

                    {editing.linkType === "feedback" && (
                      <div>
                        <Form.Label className="fw-semibold small">
                          Select Feedback Survey
                        </Form.Label>

                        {surveys.length === 0 ? (
                          <Alert
                            variant="warning"
                            className="py-2 mb-0"
                            style={{ fontSize: "0.82rem" }}
                          >
                            No active surveys found.
                          </Alert>
                        ) : (
                          <Form.Select
                            value={editing.surveyId}
                            onChange={(e) =>
                              setEditing((p) => ({
                                ...p,
                                surveyId: e.target.value,
                              }))
                            }
                          >
                            <option value="">Select survey...</option>
                            {surveys.map((s) => (
                              <option key={s.surveyid} value={s.surveyid}>
                                {s.title}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      </div>
                    )}

                    {editing.linkType === "landing" && (
                      <div>
                        <Form.Label className="fw-semibold small">
                          Select Offer Page
                        </Form.Label>

                        {landingPages.length === 0 ? (
                          <Alert
                            variant="warning"
                            className="py-2 mb-0"
                            style={{ fontSize: "0.82rem" }}
                          >
                            No offer pages yet.
                          </Alert>
                        ) : (
                          <Form.Select
                            value={editing.landingPageId}
                            onChange={(e) =>
                              setEditing((p) => ({
                                ...p,
                                landingPageId: e.target.value,
                              }))
                            }
                          >
                            <option value="">Select page...</option>
                            {landingPages.map((lp) => (
                              <option key={lp.id} value={lp.id}>
                                {lp.title}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      </div>
                    )}

                    {editing.linkType === "custom" && (
                      <div>
                        <Form.Label className="fw-semibold small">
                          Custom URL
                        </Form.Label>
                        <Form.Control
                          type="url"
                          value={editing.customUrl}
                          onChange={(e) =>
                            setEditing((p) => ({
                              ...p,
                              customUrl: e.target.value,
                            }))
                          }
                          placeholder="https://your-booking-engine.com/reserve"
                        />
                      </div>
                    )}

                    {editing.linkType === "none" && (
                      <div
                        className="text-muted text-center py-2"
                        style={{ fontSize: "0.8rem" }}
                      >
                        No link. Remove <code>{"{{link}}"}</code> or pick a type
                        above.
                      </div>
                    )}
                  </div>

                  {editing.channel === "WhatsApp" && (
                    <Alert
                      variant="warning"
                      className="py-2 mt-3 mb-0"
                      style={{ fontSize: "0.8rem" }}
                    >
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      WhatsApp templates must be{" "}
                      <strong>pre-approved by Meta</strong> via your provider
                      before sending.
                    </Alert>
                  )}
                </Col>

                {/* Right */}
                <Col md={6}>
                  <div className="fw-semibold mb-3">
                    <FaEye className="text-primary me-2" />
                    Live Preview
                  </div>

                  {editing.channel === "WhatsApp" && editing.headerImage && (
                    <div
                      style={{
                        borderRadius: "12px 12px 0 0",
                        overflow: "hidden",
                        marginBottom: 0,
                      }}
                    >
                      <img
                        src={editing.headerImage}
                        alt="Header"
                        style={{
                          width: "100%",
                          maxHeight: 160,
                          objectFit: "cover",
                          display: "block",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}

                  <MessagePreview template={editing} link={resolvedLink} />

                  {editing.content.length > 0 && editing.linkType !== "none" && (
                    <div
                      className="mt-4 p-3 rounded border"
                      style={{ background: "#f8f9fa" }}
                    >
                      <div
                        className="fw-semibold mb-2"
                        style={{ fontSize: "0.84rem" }}
                      >
                        <i className="fas fa-route text-primary me-2"></i>
                        Guest Experience Flow
                      </div>

                      <div
                        className="d-flex align-items-center flex-wrap gap-2"
                        style={{ fontSize: "0.76rem" }}
                      >
                        <span
                          className="px-2 py-1 rounded text-white"
                          style={{ background: CHANNEL_COLOR[editing.channel] }}
                        >
                          Receives {editing.channel}
                        </span>

                        <FaArrowRight className="text-muted" size={11} />
                        <span className="px-2 py-1 rounded bg-warning text-dark">
                          Taps link
                        </span>
                        <FaArrowRight className="text-muted" size={11} />

                        {editing.linkType === "feedback" && editing.surveyId && (
                          <span className="px-2 py-1 rounded bg-success text-white">
                            {surveys.find(
                              (s) =>
                                String(s.surveyid) === String(editing.surveyId)
                            )?.title || "Feedback Form"}
                          </span>
                        )}

                        {editing.linkType === "landing" &&
                          editing.landingPageId && (
                            <span className="px-2 py-1 rounded bg-primary text-white">
                              {landingPages.find(
                                (lp) =>
                                  String(lp.id) ===
                                  String(editing.landingPageId)
                              )?.title || "Offer Page"}
                            </span>
                          )}

                        {editing.linkType === "custom" && editing.customUrl && (
                          <span className="px-2 py-1 rounded bg-secondary text-white">
                            Custom URL
                          </span>
                        )}

                        <FaArrowRight className="text-muted" size={11} />
                        <span className="px-2 py-1 rounded bg-dark text-white">
                          📊 Tracked
                        </span>
                      </div>
                    </div>
                  )}

                  <div
                    className="mt-3 p-3 rounded bg-light border"
                    style={{ fontSize: "0.75rem" }}
                  >
                    <strong>Preview values:</strong>
                    <br />
                    <code>{"{{guest_name}}"}</code> → Rahul Sharma
                    <br />
                    <code>{"{{hotel_name}}"}</code> → Grand Netshine Hotel
                    <br />
                    <code>{"{{link}}"}</code> →{" "}
                    {resolvedLink
                      ? `${resolvedLink.url?.substring(0, 40)}…`
                      : "no link set"}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete <strong>{confirmData?.name}</strong>? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            No
          </Button>
          <Button variant="danger" onClick={deleteTemplate}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .template-card {
          transition: all 0.25s ease;
          border-radius: 12px !important;
        }

        .template-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.1) !important;
          border-left: 4px solid #0d6efd !important;
        }

        .template-card .card-footer {
          border-top: 1px solid #f0f0f0 !important;
        }

        .template-blank-card {
          border: 2px dashed #0d6efd !important;
          border-radius: 12px !important;
          background: #f8faff !important;
          transition: all 0.3s ease;
        }

        .template-blank-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.12) !important;
          background: #eef6ff !important;
        }

        .page-header {
          margin-bottom: 1.5rem;
        }

        @media (max-width: 992px) {
          .page-header .d-flex {
            align-items: flex-start !important;
          }
        }

        @media (max-width: 900px) {
          .editor-panel-mobile-fallback {
            width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default TemplateManager;